type TurnstileRenderOptions = {
  sitekey: string
  execution: 'execute'
  appearance: 'interaction-only'
  theme: 'light'
  size: 'flexible'
  callback: (token: string) => void
  'error-callback': () => void
  'expired-callback': () => void
  'timeout-callback': () => void
  'before-interactive-callback': () => void
}

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string
  execute: (widgetId: string) => void
  reset: (widgetId: string) => void
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

export const TURNSTILE_FIELD = 'cf-turnstile-response'
export const TURNSTILE_CONTAINER_ID = 'turnstile-container'

const SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const UNATTENDED_TIMEOUT_MS = 10_000

let scriptLoad: Promise<TurnstileApi> | null = null
let widgetId: string | null = null
let widgetContainer: HTMLElement | null = null
let claimedContainer: HTMLElement | null = null
let unattended: ReturnType<typeof setTimeout> | undefined
let settle: ((token: string | null) => void) | null = null
let queue: Promise<unknown> = Promise.resolve()

function loadScript(): Promise<TurnstileApi> {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile)
  }

  scriptLoad ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_URL
    script.async = true
    script.dataset.cookieconsent = 'necessary'
    script.onload = () =>
      window.turnstile
        ? resolve(window.turnstile)
        : reject(new Error('Turnstile did not initialise'))
    script.onerror = () => {
      scriptLoad = null
      reject(new Error('Turnstile failed to load'))
    }
    document.head.appendChild(script)
  })

  return scriptLoad
}

export function claimTurnstileContainer(container: HTMLElement): void {
  claimedContainer = container
}

export function releaseTurnstileContainer(container: HTMLElement): void {
  if (claimedContainer === container) {
    claimedContainer = null
  }
}

function requestToken(
  api: TurnstileApi,
  siteKey: string
): Promise<string | null> {
  const container =
    claimedContainer ?? document.getElementById(TURNSTILE_CONTAINER_ID)

  if (!container) {
    return Promise.resolve(null)
  }

  if (widgetId !== null && widgetContainer !== container) {
    const stale = widgetId
    widgetId = null
    api.remove(stale)
  }

  return new Promise<string | null>((resolve) => {
    unattended = setTimeout(() => settle?.(null), UNATTENDED_TIMEOUT_MS)

    settle = (token) => {
      clearTimeout(unattended)
      settle = null
      resolve(token)
    }

    if (widgetId === null) {
      widgetId = api.render(container, {
        sitekey: siteKey,
        execution: 'execute',
        appearance: 'interaction-only',
        theme: 'light',
        size: 'flexible',
        callback: (token) => settle?.(token),
        'error-callback': () => settle?.(null),
        'expired-callback': () => settle?.(null),
        'timeout-callback': () => settle?.(null),
        'before-interactive-callback': () => clearTimeout(unattended),
      })
      widgetContainer = container
    } else {
      api.reset(widgetId)
    }

    api.execute(widgetId)
  })
}

export function getTurnstileToken(): Promise<string | null> {
  const siteKey = import.meta.env.GLP_TURNSTILE_SITE_KEY

  if (!siteKey) {
    return Promise.resolve(null)
  }

  const next = queue.then(async () => {
    try {
      return await requestToken(await loadScript(), siteKey)
    } catch {
      return null
    }
  })

  queue = next

  return next
}

export async function withTurnstileToken<T extends object>(
  body: T
): Promise<T & { [TURNSTILE_FIELD]?: string }> {
  const token = await getTurnstileToken()

  return token === null ? body : { ...body, [TURNSTILE_FIELD]: token }
}
